// API-klient for kundeportalen – bruker customer_token cookie.
// Separat fra hoved api.ts for å unngå staff-refresh-logikk.

const BASE = '/api';

export class PortalApiError extends Error {
  constructor(public status: number, public body: any) {
    super(body?.message ?? `HTTP ${status}`);
    this.name = 'PortalApiError';
  }
}

async function safeJson(res: Response): Promise<any> {
  try { return await res.json(); } catch { return { message: res.statusText }; }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(options.headers ?? {}) },
    ...options,
  });
  if (!res.ok) {
    if (res.status === 401) {
      const p = window.location.pathname;
      if (!p.startsWith('/portal/login')) window.location.href = '/portal/login';
    }
    throw new PortalApiError(res.status, await safeJson(res));
  }
  return res.status === 204 ? (undefined as T) : res.json();
}

export const portalApi = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
};

export interface PortalCustomer { id: string; navn: string | null; epost: string; firma: string | null; }
export interface PortalTicket {
  id: string; sakNr: number; tittel: string; status: string; prioritet: string;
  kategori: string | null; opprettet: string; oppdatert: string;
  _count?: { messages: number };
}
export interface PortalMessage {
  id: string; type: string; innholdHtml: string | null; innholdTekst: string | null;
  opprettet: string; fromEmail: string | null;
  attachments: Array<{ id: string; filnavn: string; mimeType: string; storrelse: number }>;
}
export interface PortalTicketDetail extends PortalTicket {
  messages: PortalMessage[];
  attachments: Array<{ id: string; filnavn: string; mimeType: string; storrelse: number }>;
  beskrivelse: string | null;
}
export interface PortalCategory { id: string; navn: string; beskrivelse: string | null; }

export const portalAuth = {
  sendCode: (epost: string) => portalApi.post<{ success: true }>('/portal/auth/send-code', { epost }),
  verify: (epost: string, kode: string) => portalApi.post<{ customer: PortalCustomer }>('/portal/auth/verify', { epost, kode }),
  logout: () => portalApi.post<{ success: true }>('/portal/auth/logout'),
};

export const portalTickets = {
  list: () => portalApi.get<PortalTicket[]>('/portal/tickets'),
  get: (id: string) => portalApi.get<PortalTicketDetail>(`/portal/tickets/${id}`),
  create: (data: { tittel: string; beskrivelse?: string; kategori?: string }) =>
    portalApi.post<PortalTicket>('/portal/tickets', data),
  addComment: (ticketId: string, innhold: string) =>
    portalApi.post<{ id: string }>(`/portal/tickets/${ticketId}/comments`, { innhold }),
};

export const portalCategories = {
  list: () => portalApi.get<PortalCategory[]>('/portal/categories'),
};
